
import { computed, defineComponent, onMounted } from "vue";
import { setCurrentPageTitle } from "@/core/helpers/breadcrumb";
import ApiService from "@/core/services/ApiService";
import { ElMessageBox, ElNotification } from "element-plus";
import { useRouter } from "vue-router";
import store from "@/store";

export default defineComponent({
  name: "LoanDetails",
  setup() {
    onMounted(() => {
      setCurrentPageTitle("Зээлийн дэлгэрэнгүй");
    });

    const role = computed(() => {
      return store.getters.getRoleNames;
    });

    const isMbUser = computed(() => {
      return store.getters.getMbUser;
    });

    return {
      isMbUser,
      role
    };
  },
  mounted() {
    if (
      this.role.includes("ROLE_MORE_TLOAN") ||
      this.role.includes("ROLE_MORE_MORT_INFO") ||
      this.role.includes("ROLE_ADMIN")
    ) {
      this.getLoanDetails();
    }
  },
  data() {
    const router = useRouter();
    const idArr = this.b64DecodeUnicode(this.$route.params.id)
      .toString()
      .split("@");
    const loancode = idArr[0];
    const orgcode = idArr[1];
    const customercode = idArr[2];
    const detailType = idArr[3] === "loanline" ? "loanline" : "loan";

    return {
      router,
      loancode,
      orgcode,
      customercode,
      detailType,
      dataLoading: true,
      loanDetailsNotFound: false,
      emptyText: "Өгөгдөл байхгүй байна.",
      tableSearch: "",
      loanDetails: {},
      subLoans: [],
      collaterals: [],
      relations: [],
      deletingRelationKey: "",
      schedules: [],
      payments: [],
      requiredData: {}
    };
  },
  computed: {
    isLoanLine(): boolean {
      return this.detailType === "loanline";
    },
    canDeleteRelations(): boolean {
      return this.isMbUser && this.role.includes("ROLE_DEL_RELATION_CUST");
    },
    pageTitle(): string {
      return this.isLoanLine
        ? "Зээлийн шугамын дэлгэрэнгүй"
        : "Зээлийн дэлгэрэнгүй";
    },
    displayCode(): string {
      return this.isLoanLine
        ? this.loanDetails["loanlineContractNo"] || this.loancode
        : this.loanDetails["loanContractNo"] || this.loancode;
    },
    summaryCards(): Array<{ label: string; value: string }> {
      return [
        {
          label: "Олгосон хэмжээ",
          value: this.formatMoney(this.loanDetails["amountLcy"])
        },
        {
          label: "Үлдэгдэл",
          value: this.formatMoney(this.loanDetails["balanceLcy"])
        },
        { label: "Валют", value: this.loanDetails["currency"] || "-" },
        {
          label: "Төлөв",
          value: this.isLoanLine
            ? this.loanDetails["loanlineStatusName"] || "-"
            : this.loanDetails["loanStatusName"] || "-"
        }
      ];
    },
    detailFields(): Array<{ label: string; value: string }> {
      if (this.isLoanLine) {
        return [
          { label: "Шугамын дугаар", value: this.loanDetails["loanlineCode"] },
          {
            label: "Гэрээний дугаар",
            value: this.loanDetails["loanlineContractNo"]
          },
          {
            label: "Гэрээний огноо",
            value: this.loanDetails["loanlineContractDate"]
          },
          { label: "Төрөл", value: this.loanDetails["loanlineTypeName"] },
          { label: "Зориулалт", value: this.loanDetails["loanlineSectorName"] },
          {
            label: "Хамаарах зээлийн ангилал",
            value: this.loanDetails["childLoanClassName"]
          },
          { label: "Хүү (%)", value: this.loanDetails["interestRate"] },
          {
            label: "Үүрэг амлалтын хүү (%)",
            value: this.loanDetails["commitmentRate"]
          },
          { label: "Олгосон байгууллага", value: this.loanDetails["orgName"] },
          { label: "Олгосон огноо", value: this.loanDetails["starteddate"] },
          { label: "Төлөгдөх огноо", value: this.loanDetails["expdate"] },
          { label: "Төлөгдсөн огноо", value: this.loanDetails["paiddate"] },
          { label: "Тайлбар", value: this.loanDetails["description"] }
        ];
      }

      return [
        { label: "Зээлийн дугаар", value: this.loanDetails["loanCode"] },
        { label: "Хамаарах шугам", value: this.loanDetails["parentCode"] },
        { label: "Гэрээний дугаар", value: this.loanDetails["loanContractNo"] },
        {
          label: "Гэрээний огноо",
          value: this.loanDetails["loanContractDate"]
        },
        {
          label: "Төрөл",
          value: `${this.loanDetails["loanTypeName"] || ""}${
            this.loanDetails["subLoanTypeName"]
              ? " - " + this.loanDetails["subLoanTypeName"]
              : ""
          }`
        },
        { label: "Ангилал", value: this.loanDetails["loanClassName"] },
        { label: "Зориулалт", value: this.loanDetails["loanSectorName"] },
        { label: "Төлөв", value: this.loanDetails["loanStatusName"] },
        {
          label: "Шийдвэрлэсэн байдал",
          value: this.loanDetails["loanDecisionName"]
        },
        { label: "Хүү (%)", value: this.loanDetails["interestRate"] },
        {
          label: "Нэмэгдсэн хүү (%)",
          value: this.loanDetails["additionalInterestRate"]
        },
        { label: "Олгосон байгууллага", value: this.loanDetails["orgName"] },
        { label: "Олгосон огноо", value: this.loanDetails["starteddate"] },
        { label: "Төлөгдөх огноо", value: this.loanDetails["expdate"] },
        { label: "Төлөгдсөн огноо", value: this.loanDetails["paiddate"] },
        {
          label: "Зээл төлөлтийн хэлбэр",
          value: this.loanDetails["scheduleTypeName"]
        },
        { label: "Тайлбар", value: this.loanDetails["description"] }
      ];
    }
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    formatMoney(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      return new Intl.NumberFormat("mn-MN", {
        maximumFractionDigits: 2
      }).format(Number(value));
    },
    filteredRows(rows) {
      const search = (this.tableSearch || "")
        .toString()
        .trim()
        .toLowerCase();
      if (!search) {
        return rows || [];
      }

      return (rows || []).filter(row => {
        return Object.values(row || {}).some(value => {
          if (Array.isArray(value)) {
            return value.some(item =>
              Object.values(item || {}).some(innerValue =>
                String(innerValue ?? "")
                  .toLowerCase()
                  .includes(search)
              )
            );
          }

          return String(value ?? "")
            .toLowerCase()
            .includes(search);
        });
      });
    },
    openSubLoan(row) {
      const id = (this as any).b64EncodeUnicode(
        `${row.loanCode}@${row.providerRegnum ||
          this.orgcode}@${row.identityNo || this.customercode}@loan`
      );
      this.$router.push({
        name: "refloandetails",
        params: { id }
      });
    },
    relationKey(row) {
      return [
        row.loanCode,
        row.relationIdentityNo,
        row.relationMode,
        row.relationType
      ].join("|");
    },
    async confirmDeleteRelation(row) {
      let reason = "";
      try {
        const result = await ElMessageBox.prompt(
          "Энэ холбоотой этгээдийг устгах хүсэлтийн тайлбар оруулна уу.",
          "Баталгаажуулах",
          {
            confirmButtonText: "Устгах",
            cancelButtonText: "Болих",
            inputPlaceholder: "Тайлбар",
            inputType: "textarea",
            inputValidator: value => {
              return !!value?.trim() || "Тайлбар оруулна уу.";
            },
            type: "warning"
          }
        );
        reason = result.value.trim();
      } catch {
        return;
      }

      const key = this.relationKey(row);
      this.deletingRelationKey = key;

      try {
        const response = await ApiService.delete("refloandetails", {
          params: {
            loancode: row.loanCode || this.loancode,
            customercode: this.customercode,
            relationIdentityNo: row.relationIdentityNo,
            relationMode: row.relationMode,
            relationType: row.relationType,
            reason
          }
        });
        const result = response.data;

        if (result.code === 1) {
          ElNotification({
            title: "Хүсэлт үүслээ",
            message: result.response,
            type: "success"
          });
          return;
        }

        ElNotification({
          title:
            result.code === 401 || result.code === 2 ? "Анхааруулга" : "Алдаа",
          message: result.warning || result.error,
          type: result.code === 401 || result.code === 2 ? "warning" : "error"
        });
      } catch (error) {
        ElNotification({
          title: "Алдаа",
          message: "Холбогдох этгээд устгах хүсэлт үүсгэхэд алдаа гарлаа.",
          type: "error"
        });
      } finally {
        this.deletingRelationKey = "";
      }
    },
    async getLoanDetails() {
      this.dataLoading = true;
      try {
        const response = await ApiService.get(
          "refloandetails",
          `?loancode=${this.loancode}&customercode=${this.customercode}&orgcode=${this.orgcode}&type=${this.detailType}`
        );
        const result = response.data;

        if (result.code === 0) {
          ElNotification({
            title: "Алдаа",
            message: result.error,
            type: "error"
          });
          return;
        }

        if (result.code === 2 || result.code === 401) {
          this.loanDetailsNotFound = result.code === 2;
          ElNotification({
            title: "Анхааруулга",
            message: result.warning,
            type: "warning"
          });
          return;
        }

        this.detailType = result.response.detailType || this.detailType;
        this.loanDetails = result.response.detail || {};
        this.subLoans = result.response.subLoans || [];
        this.collaterals = result.response.collaterals || [];
        this.relations = result.response.relations || [];
        this.schedules = result.response.schedules || [];
        this.payments = result.response.payments || [];
        this.requiredData = result.response.requireddata || {};
        this.loanDetailsNotFound =
          !this.loanDetails || !Object.keys(this.loanDetails).length;
      } catch (error) {
        ElNotification({
          title: "Алдаа",
          message: "Өгөгдөл уншихад алдаа гарлаа.",
          type: "error"
        });
      } finally {
        this.dataLoading = false;
      }
    }
  }
});
